<template>
  <transition name="codice-fade">
    <div v-if="visible"
         class="onboarding-backdrop"
         @click.self="dismiss"
         role="dialog"
         aria-modal="true"
         :aria-labelledby="'onboarding-title'">
      <transition name="codice-rise" appear>
        <div v-if="visible" class="onboarding-modal" role="document">
          <!-- Double-frame ornament corners -->
          <span class="corner corner-tl" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="16" height="16">
              <path d="M1 15 V8 H4 V5 H7 V2 H15"
                    fill="none" stroke="currentColor" stroke-width="1.2"
                    stroke-linecap="square" stroke-linejoin="miter"/>
            </svg>
          </span>
          <span class="corner corner-tr" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="16" height="16">
              <path d="M15 15 V8 H12 V5 H9 V2 H1"
                    fill="none" stroke="currentColor" stroke-width="1.2"
                    stroke-linecap="square" stroke-linejoin="miter"/>
            </svg>
          </span>
          <span class="corner corner-bl" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="16" height="16">
              <path d="M1 1 V8 H4 V11 H7 V14 H15"
                    fill="none" stroke="currentColor" stroke-width="1.2"
                    stroke-linecap="square" stroke-linejoin="miter"/>
            </svg>
          </span>
          <span class="corner corner-br" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="16" height="16">
              <path d="M15 1 V8 H12 V11 H9 V14 H1"
                    fill="none" stroke="currentColor" stroke-width="1.2"
                    stroke-linecap="square" stroke-linejoin="miter"/>
            </svg>
          </span>

          <!-- Header: language toggle (left) + close (right) -->
          <div class="modal-header">
            <div class="lang-toggle" role="group" aria-label="Language">
              <button type="button"
                      class="lang-btn"
                      :class="{ active: lang === 'es' }"
                      @click="setLang('es')"
                      aria-label="Español">ES</button>
              <span class="lang-sep" aria-hidden="true">|</span>
              <button type="button"
                      class="lang-btn"
                      :class="{ active: lang === 'en' }"
                      @click="setLang('en')"
                      aria-label="English">EN</button>
            </div>
            <button type="button"
                    class="close-btn"
                    @click="dismiss"
                    :aria-label="t.closeAria">
              <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
                <path d="M4 4 L16 16 M16 4 L4 16"
                      fill="none" stroke="currentColor" stroke-width="1.4"
                      stroke-linecap="round"/>
              </svg>
            </button>
          </div>

          <!-- Title + glyphic divider -->
          <h2 id="onboarding-title" class="modal-title">{{ t.title }}</h2>
          <div class="divider" aria-hidden="true">
            <svg viewBox="0 0 120 12" width="120" height="12">
              <path d="M2 6 H38 M44 6 L52 2 L60 6 L52 10 Z M66 6 H102"
                    fill="none" stroke="currentColor" stroke-width="1"
                    stroke-linecap="round" stroke-linejoin="miter"/>
              <circle cx="40" cy="6" r="1.3" fill="currentColor"/>
              <circle cx="64" cy="6" r="1.3" fill="currentColor"/>
              <path d="M52 3.5 L57.5 6 L52 8.5 L46.5 6 Z" fill="currentColor" opacity="0.35"/>
            </svg>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <p class="lede">{{ t.intro1 }}</p>
            <p>{{ t.intro2 }}</p>

            <h3 class="rubric">{{ t.howToReadTitle }}</h3>
            <p>{{ t.howToRead1 }}</p>
            <p>{{ t.howToRead2 }}</p>

            <h3 class="rubric">{{ t.howToInteractTitle }}</h3>
            <p>{{ t.howToInteract }}</p>
          </div>

          <div class="cta-wrap">
            <button type="button"
                    class="start-btn"
                    @click="dismiss">
              {{ t.cta }}
            </button>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
const STORAGE_KEY = 'nepo:onboarded'
const LANG_KEY = 'nepo:lang'

const translations = {
  es: {
    title: '¿Qué es el Nepohualtzintzin?',
    intro1: 'El Nepohualtzintzin es un ábaco mesoamericano de origen maya y azteca, utilizado mucho antes del contacto europeo para realizar cálculos aritméticos y observaciones astronómicas.',
    intro2: 'Opera bajo un sistema vigesimal (base 20), inspirado en los veinte dedos del cuerpo humano: manos y pies.',
    howToReadTitle: 'Cómo leerlo',
    howToRead1: 'Cada columna representa una potencia de veinte (1, 20, 400, 8 000, …), leída de derecha a izquierda.',
    howToRead2: 'Cada columna posee una fila superior de 3 cuentas (cada una vale 5) y una fila inferior de 4 cuentas (cada una vale 1). Una columna activada al máximo equivale a 19.',
    howToInteractTitle: 'Cómo interactuar',
    howToInteract: 'Haz clic sobre cualquier cuenta para activarla o desactivarla. El valor decimal y vigesimal se actualizan en vivo bajo el ábaco.',
    cta: 'Empezar',
    closeAria: 'Cerrar'
  },
  en: {
    title: 'What is the Nepohualtzintzin?',
    intro1: 'The Nepohualtzintzin is a Mesoamerican abacus of Maya and Aztec origin, used long before European contact for arithmetic computation and astronomical observation.',
    intro2: 'It operates on a vigesimal (base-twenty) system, inspired by the twenty digits of the human body: hands and feet.',
    howToReadTitle: 'How to read it',
    howToRead1: 'Each column represents a power of twenty (1, 20, 400, 8,000, …), read from right to left.',
    howToRead2: 'Each column has an upper row of 3 beads (each worth 5) and a lower row of 4 beads (each worth 1). A fully activated column equals 19.',
    howToInteractTitle: 'How to interact',
    howToInteract: 'Click any bead to toggle it on or off. The decimal and vigesimal readings update live below the abacus.',
    cta: 'Begin',
    closeAria: 'Close'
  }
}

export default {
  name: 'onboarding-modal',
  data () {
    return {
      visible: false,
      lang: 'es'
    }
  },
  computed: {
    t () {
      return translations[this.lang] || translations.es
    }
  },
  methods: {
    setLang (lang) {
      this.lang = lang
      try {
        window.localStorage.setItem(LANG_KEY, lang)
      } catch (e) {
        // ignore: localStorage may be unavailable
      }
    },
    dismiss () {
      this.visible = false
      try {
        window.localStorage.setItem(STORAGE_KEY, '1')
      } catch (e) {
        // ignore: localStorage may be unavailable (private mode, SSR, etc.)
      }
    },
    onKeydown (e) {
      if (e.key === 'Escape' && this.visible) {
        this.dismiss()
      }
    }
  },
  mounted () {
    let dismissed = false
    try {
      dismissed = window.localStorage.getItem(STORAGE_KEY) === '1'
    } catch (e) {
      dismissed = false
    }
    try {
      const storedLang = window.localStorage.getItem(LANG_KEY)
      if (storedLang === 'es' || storedLang === 'en') {
        this.lang = storedLang
      }
    } catch (e) {
      // ignore
    }
    this.visible = !dismissed
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
  }
}
</script>

<style scoped>
/* ------------------------------------------------------------------ */
/* Backdrop — "page behind page"                                       */
/* ------------------------------------------------------------------ */
.onboarding-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(26, 24, 20, 0.6);
  -webkit-backdrop-filter: blur(4px) saturate(0.8);
  backdrop-filter: blur(4px) saturate(0.8);
  z-index: 9999;
  padding: 24px;
}

/* ------------------------------------------------------------------ */
/* Modal — page within a page, with double frame                       */
/* ------------------------------------------------------------------ */
.onboarding-modal {
  position: relative;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 44px 48px;
  background: var(--amate);
  color: var(--obsidiana);
  border: 1px solid var(--ink-trace);
  border-radius: 2px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.4) inset,
    0 12px 32px rgba(26, 24, 20, 0.18),
    0 2px 6px rgba(26, 24, 20, 0.08);
  font-family: 'Crimson Pro', Georgia, serif;
}

/* Inner cinnabar frame — second line of the double border */
.onboarding-modal::before {
  content: '';
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  bottom: 8px;
  border: 1px solid var(--cinabrio);
  opacity: 0.55;
  pointer-events: none;
  border-radius: 1px;
}

/* Faint warm wash — like aged amate paper */
.onboarding-modal::after {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at 20% 0%, var(--amate-warm), transparent 60%),
    radial-gradient(ellipse at 100% 100%, var(--amate-shadow), transparent 55%);
  opacity: 0.5;
  pointer-events: none;
  z-index: 0;
}

.onboarding-modal > * {
  position: relative;
  z-index: 1;
}

/* ------------------------------------------------------------------ */
/* Corner glyphs — small step-fret elements in cinnabar                */
/* ------------------------------------------------------------------ */
.corner {
  position: absolute;
  color: var(--cinabrio);
  opacity: 0.3;
  z-index: 2;
  line-height: 0;
}
.corner-tl { top: 14px; left: 14px; }
.corner-tr { top: 14px; right: 14px; transform: scaleX(1); }
.corner-bl { bottom: 14px; left: 14px; }
.corner-br { bottom: 14px; right: 14px; }

/* ------------------------------------------------------------------ */
/* Header: language toggle (left) + close (right)                      */
/* ------------------------------------------------------------------ */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: -8px -8px 18px;
}

.lang-toggle {
  display: inline-flex;
  align-items: center;
  font-family: 'Cinzel', 'Cinzel Decorative', Georgia, serif;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  font-variant-caps: small-caps;
  text-transform: uppercase;
}

.lang-btn {
  background: transparent;
  border: 0;
  padding: 4px 2px;
  cursor: pointer;
  color: var(--obsidiana-soft);
  font: inherit;
  letter-spacing: inherit;
  text-decoration: none;
  transition: color 0.18s ease;
}

.lang-btn:hover {
  color: var(--obsidiana);
}

.lang-btn.active {
  color: var(--cinabrio);
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-thickness: 1px;
}

.lang-sep {
  margin: 0 6px;
  color: var(--ink-trace);
}

.close-btn {
  background: transparent;
  border: 0;
  padding: 4px;
  cursor: pointer;
  color: var(--obsidiana-soft);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  transition: color 0.18s ease;
}

.close-btn:hover {
  color: var(--cinabrio);
}

/* ------------------------------------------------------------------ */
/* Title + divider                                                      */
/* ------------------------------------------------------------------ */
.modal-title {
  margin: 4px 0 14px;
  text-align: center;
  font-family: 'Cinzel Decorative', 'Cinzel', Georgia, serif;
  font-weight: 600;
  font-size: 1.6rem;
  line-height: 1.2;
  color: var(--obsidiana);
  letter-spacing: 0.02em;
}

.divider {
  display: flex;
  justify-content: center;
  margin-bottom: 22px;
  color: var(--cinabrio);
  opacity: 0.75;
}

/* ------------------------------------------------------------------ */
/* Body                                                                 */
/* ------------------------------------------------------------------ */
.modal-body {
  font-family: 'Crimson Pro', Georgia, serif;
  font-size: 1.05rem;
  line-height: 1.55;
  color: var(--obsidiana-soft);
}

.modal-body p {
  margin: 0 0 10px;
}

.modal-body .lede {
  margin-top: 0;
}

.rubric {
  margin: 24px 0 8px;
  font-family: 'Cinzel', 'Cinzel Decorative', Georgia, serif;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-variant-caps: small-caps;
  color: var(--cinabrio);
}

/* ------------------------------------------------------------------ */
/* CTA                                                                  */
/* ------------------------------------------------------------------ */
.cta-wrap {
  text-align: center;
  margin-top: 30px;
}

.start-btn {
  display: inline-block;
  background-color: var(--cinabrio);
  color: var(--amate);
  border: 0;
  border-radius: 2px;
  padding: 12px 32px;
  font-family: 'Cinzel', 'Cinzel Decorative', Georgia, serif;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-variant-caps: small-caps;
  cursor: pointer;
  transition: background-color 0.18s ease, transform 0.18s ease;
}

.start-btn:hover {
  background-color: var(--cinabrio-deep);
}

.start-btn:active {
  transform: translateY(1px);
}

.start-btn:focus-visible {
  outline: 2px solid var(--maíz);
  outline-offset: 3px;
}

/* ------------------------------------------------------------------ */
/* Scrollbar — discreet                                                 */
/* ------------------------------------------------------------------ */
.onboarding-modal::-webkit-scrollbar {
  width: 6px;
}
.onboarding-modal::-webkit-scrollbar-thumb {
  background: var(--ink-trace);
  border-radius: 3px;
}

/* ------------------------------------------------------------------ */
/* Transitions                                                          */
/* ------------------------------------------------------------------ */
.codice-fade-enter-active,
.codice-fade-leave-active {
  transition: opacity 280ms ease;
}
.codice-fade-enter,
.codice-fade-leave-to {
  opacity: 0;
}

.codice-rise-enter-active {
  transition: opacity 280ms ease-out 60ms, transform 280ms ease-out 60ms;
}
.codice-rise-leave-active {
  transition: opacity 200ms ease-in, transform 200ms ease-in;
}
.codice-rise-enter {
  opacity: 0;
  transform: translateY(12px);
}
.codice-rise-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* ------------------------------------------------------------------ */
/* Responsive                                                           */
/* ------------------------------------------------------------------ */
@media (max-width: 520px) {
  .onboarding-modal {
    padding: 36px 28px;
  }
  .modal-title {
    font-size: 1.35rem;
  }
  .modal-body {
    font-size: 1rem;
  }
}
</style>



// WEBPACK FOOTER //
// OnboardingModal.vue?17709114